import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import {
    useDataSourceConnectionTypes,
    useActivityMetadataTypes,
} from "../../api/data-sources";
import {
    useActivityDataRawMaterial,
    useActivityDataUnits,
} from "../../api/activity-data";
import Button from "../Button";
import FormDropdown from "../FormDropdown";
import {
    ACTIVITY_METADATA_NONE_VALUE,
    AddDataSourceFormValues,
    addActivityDataFormValidationSchema,
    defaultAddDataSourceFormValues,
} from "./AddDataSourceFormValues";
import { usePaginatedEmissionsSources } from "../../api/emissions-sources";
import Typography from "../Typography";
import ContentContainer from "../ContentContainer";

type AddDataSourceFormProps = {
    onSubmit: (values: AddDataSourceFormValues) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    initialValues?: Partial<AddDataSourceFormValues>;
};

const AddDataSourceForm = ({
    onSubmit,
    onCancel,
    isSubmitting,
    initialValues,
}: AddDataSourceFormProps) => {
    const { emissionsSources, isLoading: isLoadingEmissionsSources } =
        usePaginatedEmissionsSources({
            isMaterialOverride: true,
            dataStatusOverrideIds: [3, 4],
            hasLinkedDataSource: false,
            initialPageSize: 100,
        });
    const { activityMetadataTypes, isLoading: isLoadingActivityMetadataTypes } =
        useActivityMetadataTypes();
    const {
        dataSourceConnectionTypes,
        isLoading: isLoadingDataSourceConnectionTypes,
    } = useDataSourceConnectionTypes();
    const { activityDataUnits, isLoading: isLoadingActivityDataUnits } =
        useActivityDataUnits();

    const {
        activityDataRawMaterials,
        isLoading: isLoadingActivityDataRawMaterials,
    } = useActivityDataRawMaterial();

    const methods = useForm<AddDataSourceFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(addActivityDataFormValidationSchema),
        defaultValues: { ...defaultAddDataSourceFormValues, ...initialValues },
    });

    const emissionsSourceOptions = emissionsSources
        ? emissionsSources.map((emissionsSources) => ({
              value: emissionsSources.id,
              label: emissionsSources.name,
          }))
        : [];

    const activityMetadataTypeOptions = activityMetadataTypes
        ? [{ value: ACTIVITY_METADATA_NONE_VALUE, label: "None" }].concat(
              activityMetadataTypes.map((activityMetadataType) => ({
                  value: activityMetadataType.id,
                  label: activityMetadataType.name,
              }))
          )
        : [];

    const dataSourceConnectionTypeOptions = dataSourceConnectionTypes
        ? dataSourceConnectionTypes.map((dataSourceConnectionType) => ({
              value: dataSourceConnectionType.id,
              label: dataSourceConnectionType.name,
          }))
        : [];

    const activityDataUnitsOptions = activityDataUnits
        ? activityDataUnits.map((activityDataUnit) => ({
              value: activityDataUnit.id,
              label: activityDataUnit.suffix,
          }))
        : [];

    const activityDataRawMaterialOptions = activityDataRawMaterials
        ? activityDataRawMaterials.map((rawMaterial) => ({
              value: rawMaterial.id,
              label: rawMaterial.name,
          }))
        : [];

    if (isLoadingEmissionsSources) {
        return <p className="text-center">Loading...</p>;
    }

    if (emissionsSources.length === 0) {
        return (
            <ContentContainer className="pt-14">
                <Typography
                    variant="body-large"
                    component="h1"
                    className="text-deep-ocean-20 text-center"
                >
                    You cannot create a data source at the moment as there are
                    no emissions sources available to link it to.
                    <br />
                    Please create an emissions source first.
                </Typography>
            </ContentContainer>
        );
    }

    const onSubmitHandler = (values: AddDataSourceFormValues) => {
        if (values.activityMetadataTypeId === ACTIVITY_METADATA_NONE_VALUE) {
            values.activityMetadataTypeId = undefined;
        }
        onSubmit(values);
    };

    return (
        <form
            onSubmit={methods.handleSubmit(onSubmitHandler)}
            className="space-y-4 max-w-xl rounded-xl bg-surface-light-1 p-6 mx-auto"
        >
            <Controller
                name="emissionsSourceId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDropdown
                        required
                        isLoadingOptions={isLoadingEmissionsSources}
                        label="Emissions Source"
                        pleaseSelectText="Select emissions source"
                        options={emissionsSourceOptions}
                        value={value}
                        onChange={onChange}
                        error={methods.formState.errors.emissionsSourceId}
                    />
                )}
            />
            <Controller
                name="connectionTypeId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDropdown
                        isLoadingOptions={isLoadingDataSourceConnectionTypes}
                        label="Connection Type"
                        pleaseSelectText="Select connection type"
                        options={dataSourceConnectionTypeOptions}
                        value={value}
                        onChange={onChange}
                        error={methods.formState.errors.connectionTypeId}
                    />
                )}
            />
            <Controller
                name="rawUnitsId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDropdown
                        required
                        isLoadingOptions={isLoadingActivityDataUnits}
                        label="Raw units"
                        pleaseSelectText="Select raw units"
                        options={activityDataUnitsOptions}
                        value={value}
                        onChange={onChange}
                        error={methods.formState.errors.rawUnitsId}
                    />
                )}
            />
            <Controller
                name="rawMaterialId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDropdown
                        required
                        isLoadingOptions={isLoadingActivityDataRawMaterials}
                        label="Raw material"
                        pleaseSelectText="Select raw material"
                        options={activityDataRawMaterialOptions}
                        value={value}
                        onChange={onChange}
                        error={methods.formState.errors.rawMaterialId}
                    />
                )}
            />
            {activityMetadataTypeOptions.length > 0 && (
                <Controller
                    name="activityMetadataTypeId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            isLoadingOptions={isLoadingActivityMetadataTypes}
                            label="Activity Metadata Type"
                            pleaseSelectText="Select type"
                            options={activityMetadataTypeOptions}
                            value={value}
                            onChange={onChange}
                            error={
                                methods.formState.errors.activityMetadataTypeId
                            }
                        />
                    )}
                />
            )}
            <div className="flex justify-end gap-6 pt-4">
                <Button
                    variant="transparent"
                    type="button"
                    disabled={isSubmitting}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                    Add data source
                </Button>
            </div>
        </form>
    );
};

export default AddDataSourceForm;
